/**
 * axe DevTools — web accessibility testing
 *
 * Declares a single web-accessible-resource entry — `highlighter.js`, two sourcemaps
 * and a pinned set of `axe-versions/<ver>/axe.js` engine bundles — matched to every
 * origin with no `use_dynamic_url`. All are fetchable from any page at a fixed URL.
 *
 * There are no images exposed, so the primary is a fetch of `highlighter.js`, a stable
 * vendor-named script. The versioned engine paths corroborate: they are explicit,
 * hand-listed version numbers (not build-hashed names), so they persist across releases.
 *
 * Manifest verified against version 4.132.1.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'lhdoppojpmngadmnindnejefpokejbdd';

export const axeDevtools: ExtensionDefinition = {
  id: ID,
  name: 'axe DevTools - Web Accessibility Testing',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'Exposes highlighter.js and pinned axe-versions engine bundles to every origin with ' +
    'no dynamic URL. No images, so detection is a fetch — conclusive on stable paths.',
  signals: [
    {
      id: 'war-highlighter-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch highlighter.js — a stable vendor-named script exposed to every origin. ' +
        'A fixed path, distinctive to axe DevTools.',
      weight: 1,
      run: () => probeFetch(ID, 'highlighter.js'),
    },
    {
      id: 'war-axe-latest-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch axe-versions/latest/axe.js — the bundled axe-core engine at a stable ' +
        'path, corroborating the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'axe-versions/latest/axe.js'),
    },
    {
      id: 'war-axe-4-11-4-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch axe-versions/4.11.4/axe.js — a hand-listed pinned engine version, a ' +
        'distinctive third path that is not build-hashed.',
      weight: 0.5,
      run: () => probeFetch(ID, 'axe-versions/4.11.4/axe.js'),
    },
  ],
};
